import type { Terminal } from '@otw/models/leads/terminals'
import { db } from '@otw/models/db'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { addDoc, deleteDoc, doc, getDoc, getDocs, setDoc } from 'firebase/firestore'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { usePickupLocations } from 'hooks/inventory/usePickupLocations'
import {
  buildTerminalLocationPayload,
  type TerminalAddressInput,
} from '@/core/terminals/terminalAddress'

export const terminalsQueryKey = ['terminals'] as const

export interface CreateTerminalInput {
  name?: string
  abbreviation?: string
  number?: string
  notes?: string
  /**
   * Adopt an existing `pickup_locations` doc. When provided, the location is
   * not modified. Mutually exclusive with {@link address} in practice —
   * `address` wins if both are set and a new location is needed.
   */
  pickup_location_id?: string | null
  /** When provided (and no existing location id), creates a new pickup_locations doc. */
  address?: TerminalAddressInput
  /** Company ids to associate with this terminal on create (writes company.terminalIds). */
  companyIds?: string[]
}

export interface UpdateTerminalInput {
  id: string
  name?: string
  abbreviation?: string
  number?: string
  notes?: string
  /** Repoint to a different existing location without writing through. */
  pickup_location_id?: string | null
  /** When provided, write through address fields to the linked pickup_locations doc. */
  address?: TerminalAddressInput
  /** Replace the set of associated company ids. */
  companyIds?: string[]
}

const sortTerminalsForList = (terminals: Terminal[]): Terminal[] =>
  [...terminals].sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())

const trimOptional = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim()
  return trimmed || undefined
}

const buildTerminalLabel = (input: {
  name?: string
  abbreviation?: string
  number?: string
}): string => {
  const name = trimOptional(input.name) ?? ''
  const abbrev = trimOptional(input.abbreviation) ?? ''
  const number = trimOptional(input.number) ?? ''
  if (name) return name
  if (abbrev && number) return `${abbrev} #${number}`
  if (abbrev) return abbrev
  if (number) return `#${number}`
  return ''
}

export const useTerminals = () => {
  const { userInfo } = useAuth()
  const { showSuccess, showError } = useSnackbar()
  const queryClient = useQueryClient()
  const { createPickupLocation, updatePickupLocation } = usePickupLocations()

  const {
    data: terminals,
    isLoading,
    error,
  } = useQuery({
    queryKey: terminalsQueryKey,
    queryFn: async () => {
      const snapshot = await getDocs(db.terminals)
      return sortTerminalsForList(snapshot.docs.map((d) => d.data()))
    },
  })

  const syncCompanyTerminalLinks = async (
    terminalId: string,
    nextCompanyIds: string[] | undefined,
  ) => {
    if (nextCompanyIds === undefined) return
    const desired = new Set(
      nextCompanyIds.map((id) => id.trim()).filter(Boolean),
    )
    const companiesSnap = await getDocs(db.contact_companies)
    await Promise.all(
      companiesSnap.docs.map(async (companyDoc) => {
        const company = companyDoc.data()
        const currentIds = company.terminalIds ?? []
        const hasLink = currentIds.includes(terminalId)
        const shouldHave = desired.has(company.id)
        if (hasLink === shouldHave) return
        const terminalIds = shouldHave
          ? [...currentIds.filter((id) => id !== terminalId), terminalId]
          : currentIds.filter((id) => id !== terminalId)
        await setDoc(companyDoc.ref, {
          ...company,
          terminalIds,
          updatedAt: new Date(),
        })
      }),
    )
    await queryClient.invalidateQueries({ queryKey: ['contact_companies'] })
  }

  const createMutation = useMutation({
    mutationFn: async (input: CreateTerminalInput) => {
      const uid = userInfo?.id
      if (!uid) throw new Error('You must be signed in.')

      let pickupLocationId = input.pickup_location_id?.trim() || null

      if (input.address) {
        const label = buildTerminalLabel(input)
        const payload = buildTerminalLocationPayload(input.address, label)
        if (!pickupLocationId) {
          const created = await createPickupLocation(payload)
          pickupLocationId = created.id ?? null
        } else {
          await updatePickupLocation({
            id: pickupLocationId,
            ...payload,
          })
        }
      }

      if (!pickupLocationId) {
        throw new Error('A terminal must have an address.')
      }

      const now = new Date()
      const payload: Terminal = {
        id: '',
        pickup_location_id: pickupLocationId,
        ...(trimOptional(input.name) ? { name: trimOptional(input.name) } : {}),
        ...(trimOptional(input.abbreviation)
          ? { abbreviation: trimOptional(input.abbreviation) }
          : {}),
        ...(trimOptional(input.number) ? { number: trimOptional(input.number) } : {}),
        ...(trimOptional(input.notes) ? { notes: trimOptional(input.notes) } : {}),
        createdAt: now,
        updatedAt: now,
        createdByUserId: uid,
      }
      const docRef = await addDoc(db.terminals, payload)
      await syncCompanyTerminalLinks(docRef.id, input.companyIds)
      return docRef.id
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: terminalsQueryKey }),
        queryClient.invalidateQueries({ queryKey: ['pickupLocations'] }),
      ])
      showSuccess('Terminal created')
    },
    onError: (err: Error) => showError(err.message || 'Could not create terminal'),
  })

  const updateMutation = useMutation({
    mutationFn: async (input: UpdateTerminalInput) => {
      const ref = doc(db.terminals, input.id)
      const snap = await getDoc(ref)
      const current = snap.data()
      if (!current) throw new Error('Terminal not found')

      let pickupLocationId =
        input.pickup_location_id !== undefined
          ? input.pickup_location_id?.trim() || null
          : current.pickup_location_id

      if (input.address) {
        const label = buildTerminalLabel({
          name: input.name !== undefined ? input.name : current.name,
          abbreviation:
            input.abbreviation !== undefined ? input.abbreviation : current.abbreviation,
          number: input.number !== undefined ? input.number : current.number,
        })
        const payload = buildTerminalLocationPayload(input.address, label)
        if (pickupLocationId) {
          await updatePickupLocation({
            id: pickupLocationId,
            ...payload,
          })
        } else {
          const created = await createPickupLocation(payload)
          pickupLocationId = created.id ?? null
        }
      }

      if (!pickupLocationId) {
        throw new Error('A terminal must have an address.')
      }

      const merged: Terminal = {
        ...current,
        pickup_location_id: pickupLocationId,
        updatedAt: new Date(),
      }

      if (input.name !== undefined) {
        const name = trimOptional(input.name)
        if (name) merged.name = name
        else delete merged.name
      }
      if (input.abbreviation !== undefined) {
        const abbreviation = trimOptional(input.abbreviation)
        if (abbreviation) merged.abbreviation = abbreviation
        else delete merged.abbreviation
      }
      if (input.number !== undefined) {
        const number = trimOptional(input.number)
        if (number) merged.number = number
        else delete merged.number
      }
      if (input.notes !== undefined) {
        const notes = trimOptional(input.notes)
        if (notes) merged.notes = notes
        else delete merged.notes
      }

      await setDoc(ref, merged)
      await syncCompanyTerminalLinks(input.id, input.companyIds)
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: terminalsQueryKey }),
        queryClient.invalidateQueries({ queryKey: ['pickupLocations'] }),
      ])
      showSuccess('Terminal updated')
    },
    onError: (err: Error) => showError(err.message || 'Could not update terminal'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // Detach from all companies before deleting the terminal.
      await syncCompanyTerminalLinks(id, [])
      await deleteDoc(doc(db.terminals, id))
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: terminalsQueryKey })
      showSuccess('Terminal deleted')
    },
    onError: () => showError('Could not delete terminal'),
  })

  return {
    terminals: terminals ?? [],
    isLoading,
    error: error instanceof Error ? error : error ? new Error(String(error)) : null,
    createTerminal: (input: CreateTerminalInput) => createMutation.mutateAsync(input),
    updateTerminal: (input: UpdateTerminalInput) => updateMutation.mutateAsync(input),
    deleteTerminal: (id: string) => deleteMutation.mutateAsync(id),
    isSaving: createMutation.isPending || updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}
